// pages/Dashboard/Finances/PaymentBreakdown.tsx
import React from 'react';
import { CreditCard, Wallet } from 'lucide-react';
import { useLanguage } from '../../../hooks/useLanguage';

interface PaymentBreakdownProps {
    data: {
        revenue: { currency: string };
        commission: { rate: number };
        payments: {
            mobile: {
                count: number;
                total: number;
                commission: number;
                net: number;
            };
            onSite: {
                count: number;
                total: number;
                commission: number;
            };
        };
        balance: {
            settledToGarage: number;
            settledFromGarage: number;
        };
    };
}

export const PaymentBreakdown: React.FC<PaymentBreakdownProps> = ({ data }) => {
    const { t, isRTL } = useLanguage();
    const { revenue, commission, payments, balance } = data;

    const remainingToReceive = Math.max(0, payments.mobile.net - balance.settledToGarage);
    const remainingToPay = Math.max(0, payments.onSite.commission - balance.settledFromGarage);

    return (
        <>
            {/* Paiements Mobile */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className={`flex flex-wrap items-start sm:items-center gap-3 mb-4 sm:mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CreditCard className="text-green-600" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {t('finances.payments.mobile.title')}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {t('finances.payments.mobile.description')}
                        </p>
                    </div>
                    <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs sm:text-sm font-medium rounded-full whitespace-nowrap">
                        {t('finances.payments.quotesCount', { count: payments.mobile.count })}
                    </span>
                </div>

                <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2">
                        <span className="text-sm text-gray-600">{t('finances.payments.mobile.totalReceived')}</span>
                        <span className="font-semibold text-gray-900 text-sm sm:text-base">
                            {payments.mobile.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                        </span>
                    </div>

                    <div className="flex justify-between items-center pb-2">
                        <span className="text-sm text-gray-600">{t('finances.payments.commissionRate', { rate: commission.rate })}</span>
                        <span className="font-semibold text-purple-600 text-sm sm:text-base">
                            -{payments.mobile.commission.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                        </span>
                    </div>

                    <div className="border-t pt-3 flex justify-between items-center pb-2">
                        <span className="font-medium text-gray-700 text-sm">{t('finances.payments.mobile.netToReceive')}</span>
                        <span className="font-bold text-gray-900 text-sm sm:text-base">
                            {payments.mobile.net.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                        </span>
                    </div>

                    {balance.settledToGarage > 0 && (
                        <div className="flex justify-between items-center pb-2">
                            <span className="text-xs sm:text-sm text-green-600">✓ {t('finances.payments.alreadyReceived')}</span>
                            <span className="font-semibold text-green-600 text-sm sm:text-base">
                                -{balance.settledToGarage.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                            </span>
                        </div>
                    )}

                    <div className="border-t pt-3 bg-green-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mt-3">
                        <div className="flex justify-between items-center">
                            <span className="font-bold text-gray-900 text-sm sm:text-base">{t('finances.payments.remainingToReceive')}</span>
                            <span className="text-xl sm:text-2xl font-bold text-green-600">
                                {remainingToReceive.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                            </span>
                        </div>
                        {remainingToReceive === 0 && (
                            <p className="text-xs text-green-600 mt-1">
                                ✓ {t('finances.payments.allReceived')}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Paiements Sur Place */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className={`flex flex-wrap items-start sm:items-center gap-3 mb-4 sm:mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Wallet className="text-orange-600" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {t('finances.payments.onSite.title')}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {t('finances.payments.onSite.description')}
                        </p>
                    </div>
                    <span className="px-2 sm:px-3 py-1 bg-orange-100 text-orange-700 text-xs sm:text-sm font-medium rounded-full whitespace-nowrap">
                        {t('finances.payments.quotesCount', { count: payments.onSite.count })}
                    </span>
                </div>

                <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2">
                        <span className="text-sm text-gray-600">{t('finances.payments.onSite.totalReceived')}</span>
                        <span className="font-semibold text-gray-900 text-sm sm:text-base">
                            {payments.onSite.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                        </span>
                    </div>

                    <div className="border-t pt-3 flex justify-between items-center pb-2">
                        <span className="font-medium text-gray-700 text-sm">{t('finances.payments.onSite.commissionDue')}</span>
                        <span className="font-bold text-gray-900 text-sm sm:text-base">
                            {payments.onSite.commission.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                        </span>
                    </div>

                    {balance.settledFromGarage > 0 && (
                        <div className="flex justify-between items-center pb-2">
                            <span className="text-xs sm:text-sm text-blue-600">✓ {t('finances.payments.alreadyPaid')}</span>
                            <span className="font-semibold text-blue-600 text-sm sm:text-base">
                                -{balance.settledFromGarage.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                            </span>
                        </div>
                    )}

                    <div className="border-t pt-3 bg-orange-50 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mt-3">
                        <div className="flex justify-between items-center">
                            <span className="font-bold text-gray-900 text-sm sm:text-base">{t('finances.payments.remainingToPay')}</span>
                            <span className="text-xl sm:text-2xl font-bold text-orange-600">
                                {remainingToPay.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {revenue.currency}
                            </span>
                        </div>
                        {remainingToPay === 0 && (
                            <p className="text-xs text-green-600 mt-1">
                                ✓ {t('finances.payments.allPaid')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-800">
                        💡 {t('finances.payments.onSite.info')}
                    </p>
                </div>
            </div>
        </>
    );
};